// @flow

/**
 * Module dependencies.
 */

import { branch } from 'core/utils/branch';
import { breakpoints } from 'client/styles/layout';
import { camelCase, get, head, initial, intersection, isFunction, kebabCase, omit, pull } from 'lodash';
import { colors } from 'client/styles/colors';

/**
 * Base breakpoint.
 */

const baseBreakpoint = 'base';

/**
 * Directions properties.
 */

const directions = {
  horizontal: ['left', 'right'],
  vertical: ['bottom', 'top']
};

/**
 * Omissible DOM properties.
 */

const omissibleDOMProps = [
  'children',
  'className',
  'href',
  'id',
  'theme'
];

/**
 * Normalize CSS.
 */

export function normalizeCss(object: Object = {}, extraParams: string = ''): string {
  return Object
    .keys(object)
    .reduce((result: string, key: string): string => `
      ${result}
      ${key} {
        ${object[key]}
        ${extraParams}
      }
    `, '');
}

/**
 * Set color by prop.
 */

export function setColorProp(color: string, property?: string, defaultColor?: string): string {
  return branch(color, `${property || 'color'}: ${get(colors, color, defaultColor || colors.shadesOfBlue.oxfordBlue)};`);
}

/**
 * Set styles prop.
 * Can receive a function or a string in
 * the `styles` prop.
 */

export function setStylesProp(props: Object): string {
  if (isFunction(props.styles)) {
    return props.styles(props);
  }

  return branch(props.styles, props.styles);
}

/**
 * Get intersection in two arrays of string.
 */

function getIntersection(first: Array<string>, second: Array<string>, defaultUnmatched: any): any {
  const matches = intersection(first, second);

  return matches.length ? head(matches) : defaultUnmatched;
}

/**
 * Convert camel case string to array of strings.
 */

function camelCaseToArray(properties: string): Array<string> {
  return kebabCase(properties).split('-');
}

/**
 * Create directional styles.
 */

function createDirectionalStyles(property: string, value: any, directions: Array<string>): Object {
  return directions.reduce((style: Object, direction: string): Object => ({
    ...style,
    [`${property}-${direction}`]: value
  }), {});
}

/**
 * Get styles by breakpoint.
 */

function getStylesByBreakpoint(properties: Object, customBreakpoints: Object): Object {
  const breakpointsKeys = Object.keys(customBreakpoints);

  return Object
    .keys(properties)
    .map(camelCaseToArray)
    .reduce((group: Object, item: Array<string>): Object => {
      const breakpoint = getIntersection(item, breakpointsKeys, baseBreakpoint);
      const defaultBreakpoint = breakpoint === baseBreakpoint;
      const propertiesArray = defaultBreakpoint ? item : initial(item);
      const property = kebabCase(propertiesArray.toString());
      const value = get(properties, camelCase(item.toString()));
      const directionalFields = Object.keys(directions);
      const direction = getIntersection(item, directionalFields, false);

      let styleProps = {
        [property]: value
      };

      if (direction) {
        const [property] = pull(item, direction, ...breakpointsKeys);

        styleProps = createDirectionalStyles(property, value, directions[direction]);
      }

      return {
        ...group,
        [breakpoint]: {
          ...group[breakpoint],
          ...styleProps
        }
      };
    }, {});
}

/**
 * Render style object to styled string.
 */

function renderToStyle(objectStyle: Object, breakpoint: string): string {
  const style = Object
    .keys(objectStyle)
    .reduce((style: string, property: string): string => `${style} ${property}: ${objectStyle[property]};`, '');

  if (breakpoint) {
    return `
      @media (min-width: ${breakpoints[breakpoint]}px) {
        ${style}
      }
    `;
  }

  return style;
}

/**
 * Set styled properties.
 */

export function setStyledProps(props: Object, filteredDomProps: Array<string> = []): string {
  const styleProps = omit(props, [...omissibleDOMProps, ...filteredDomProps]);
  const stylesByBreakpoint = getStylesByBreakpoint(styleProps, breakpoints);

  return Object
    .keys(breakpoints)
    .sort((first: any, second: any): number => breakpoints[first] - breakpoints[second])
    .reduce((style: string, key: string): string => {
      const breakpoint = branch(key !== baseBreakpoint, key);
      const breakpointStyle = stylesByBreakpoint[key];

      if (!breakpointStyle) {
        return style;
      }

      return `${style} ${renderToStyle(breakpointStyle, breakpoint)}`;
    }, '');
}



// WEBPACK FOOTER //
// ../src/client/styles/utils.js